import React, { useState, useEffect } from 'react';
import { usersApi } from '../../services/api';
import Bearbeitungslog from '../logs/Bearbeitungslog';
import DeleteReasonModal from '../shared/DeleteReasonModal';
import SuspendUserModal from '../shared/SuspendUserModal';

// List of known universities for autocomplete
const UNIVERSITIES = [
  'Uni Hamburg',
  'TU Hamburg',
  'HafenCity Universität',
  'HAW Hamburg',
  'Bucerius Law School',
  'HfBK Hamburg',
  'Helmut-Schmidt-Universität',
  'Kühne Logistics University',
  'Hochschule für Musik und Theater',
  'Muthesius Kunsthochschule',
  'Christian-Albrechts-Universität Kiel',
  'HAW Kiel',
  'TH Lübeck',
  'Hochschule Flensburg',
  'Musikhochschule Lübeck',
  'HTW Berlin',
  'TU Berlin',
  'LMU Munich',
  'TUM',
  'RWTH Aachen',
  'University of Heidelberg',
  'University of Bonn',
  'University of Munich',
];

function UserDetail({ userId, onBack }) {
  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    enrolled_university: ''
  });
  const [showUniversitySuggestions, setShowUniversitySuggestions] = useState(false);
  const [universitySuggestions, setUniversitySuggestions] = useState([]);

  // OTP modal state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');

  // Issue #417: Suspend/Unsuspend modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);

  // GDPR export state (Issue #358)
  const [exportRunning, setExportRunning] = useState(false);
  const [exportPercent, setExportPercent] = useState(0);
  const [exportError, setExportError] = useState('');

  // Activities filter state
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesFilters, setActivitiesFilters] = useState({
    content_type: '',
    reported_only: false,
    search: '',
    date_from: '',
    date_to: ''
  });

  const perPage = 10;

  // Fetch user details
  const fetchUser = async () => {
    try {
      const response = await usersApi.get(userId);
      setUser(response.data);
      setEditData({
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        enrolled_university: response.data.enrolled_university || ''
      });
    } catch (err) {
      setError('Fehler beim Laden der Benutzerdaten');
      console.error('User fetch error:', err);
    }
  };

  // Fetch user activities
  const fetchActivities = async () => {
    try {
      const params = {
        page: activitiesPage,
        per_page: perPage,
        ...activitiesFilters
      };
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === false) {
          delete params[key];
        }
      });

      const response = await usersApi.getActivities(userId, params);
      setActivities(response.data.activities);
      setActivitiesTotal(response.data.total);
    } catch (err) {
      console.error('Activities fetch error:', err);
    }
  };

  // Fetch user emails (Issue #155)
  const fetchEmails = async () => {
    try {
      const response = await usersApi.getEmails(userId);
      setEmails(response.data.emails || []);
    } catch (err) {
      console.error('Emails fetch error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchUser();
      await fetchActivities();
      await fetchEmails();
      setIsLoading(false);
    };
    loadData();
  }, [userId]);

  useEffect(() => {
    fetchActivities();
  }, [activitiesPage, activitiesFilters]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUserSuspended = () => {
    if (!user?.suspended_until) return false;
    return new Date(user.suspended_until) > new Date();
  };

  const getStatusBadge = () => {
    if (!user?.email_verified) {
      return <span className="status-badge unverified">Nicht verifiziert</span>;
    }
    if (isUserSuspended()) {
      return <span className="status-badge suspended">Gesperrt</span>;
    }
    return <span className="status-badge verified">Verifiziert</span>;
  };

  // Handle university input with autocomplete
  const handleUniversityInputChange = (value) => {
    setEditData({ ...editData, enrolled_university: value });
    if (value.length > 0) {
      const filtered = UNIVERSITIES.filter(uni =>
        uni.toLowerCase().includes(value.toLowerCase())
      );
      setUniversitySuggestions(filtered);
      setShowUniversitySuggestions(filtered.length > 0);
    } else {
      setUniversitySuggestions([]);
      setShowUniversitySuggestions(false);
    }
  };

  const handleUniversitySelect = (university) => {
    setEditData({ ...editData, enrolled_university: university });
    setShowUniversitySuggestions(false);
  };

  // Save user edit
  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      await usersApi.update(userId, editData);
      await fetchUser();
      setIsEditing(false);
      setSuccessMessage('Benutzer aktualisiert');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Action handlers
  const handleAction = async (action) => {
    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      switch (action) {
        case 'activate':
          await usersApi.activate(userId);
          setSuccessMessage('Benutzer wurde verifiziert');
          break;
        case 'deactivate':
          await usersApi.deactivate(userId);
          setSuccessMessage('Verifizierung wurde entfernt');
          break;
        case 'suspend':
        case 'unsuspend':
          // Issue #417: Handled by modals via handleSuspendConfirm /
          // handleUnsuspendConfirm so a documented reason (>=20 chars)
          // is collected before the action runs.
          break;
        case 'resetPassword':
          await usersApi.resetPassword(userId);
          setSuccessMessage('Passwort-Reset-E-Mail wurde gesendet');
          await fetchEmails(); // Refresh email list (Issue #155)
          break;
        case 'resendVerification':
          await usersApi.resendVerification(userId);
          setSuccessMessage('Verifizierungs-E-Mail wurde gesendet');
          await fetchEmails(); // Refresh email list (Issue #155)
          break;
        case 'sendOtp':
          await usersApi.sendOtp(userId);
          setSuccessMessage('OTP-E-Mail wurde gesendet');
          await fetchEmails(); // Refresh email list (Issue #155)
          break;
        default:
          break;
      }
      await fetchUser();
    } catch (err) {
      setError('Aktion fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // Issue #417: Confirm suspension with days + reason (>=20 chars)
  const handleSuspendConfirm = async (days, reason) => {
    setError('');
    setSuccessMessage('');
    try {
      await usersApi.suspend(userId, days, reason);
      setShowSuspendModal(false);
      setSuccessMessage(`Benutzer für ${days} Tage gesperrt`);
      await fetchUser();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      // Re-throw so the modal can display the API error inline.
      throw err;
    }
  };

  // Issue #417: Confirm unsuspension with reason (>=20 chars)
  const handleUnsuspendConfirm = async (reason) => {
    setError('');
    setSuccessMessage('');
    try {
      await usersApi.unsuspend(userId, reason);
      setShowUnsuspendModal(false);
      setSuccessMessage('Sperre wurde aufgehoben');
      await fetchUser();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      throw err;
    }
  };

  // Set OTP manually
  const handleSetOtp = async () => {
    if (otpValue.length !== 6 || !/^\d+$/.test(otpValue)) {
      setError('OTP muss aus 6 Ziffern bestehen');
      return;
    }

    setActionLoading(true);
    try {
      await usersApi.setOtp(userId, otpValue);
      setSuccessMessage('OTP wurde gesetzt');
      setShowOtpModal(false);
      setOtpValue('');
    } catch (err) {
      setError('Fehler beim Setzen des OTP: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // GDPR data export (Issue #358): start job, poll status, trigger download
  const handleGdprExport = async () => {
    setExportError('');
    setExportRunning(true);
    setExportPercent(0);

    try {
      const startResp = await usersApi.startDataExport(userId);
      const jobId = startResp.data.job_id;

      // Poll status every second
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const statusResp = await usersApi.getDataExportStatus(userId, jobId);
        const { state, percent, error: jobError } = statusResp.data;
        setExportPercent(percent);

        if (state === 'ready') {
          break;
        }
        if (state === 'failed') {
          throw new Error(jobError || 'Export fehlgeschlagen');
        }
      }

      // Download the ZIP
      const downloadResp = await usersApi.downloadDataExport(userId, jobId);
      const blob = new Blob([downloadResp.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      // Try to read filename from Content-Disposition; fall back to a default
      let filename = `gdpr_export_user_${userId}.zip`;
      const cd = downloadResp.headers['content-disposition'];
      if (cd) {
        const match = cd.match(/filename="?([^";]+)"?/i);
        if (match && match[1]) filename = match[1];
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('DSGVO-Export fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    } finally {
      setExportRunning(false);
    }
  };

  const getContentTypeBadge = (type) => {
    switch (type) {
      case 'post':
        return <span className="type-badge post">Beitrag</span>;
      case 'marketplace':
        return <span className="type-badge marketplace">Objekt</span>;
      case 'comment':
        return <span className="type-badge info">Kommentar</span>;
      case 'course_review':
        return <span className="type-badge info">Kursbewertung</span>;
      default:
        return <span className="type-badge">{type}</span>;
    }
  };

  // Get email status badge (Issue #155)
  const getEmailStatusBadge = (status) => {
    if (status === 'success') {
      return <span className="status-badge verified">Erfolgreich</span>;
    }
    return <span className="status-badge unverified">Fehlgeschlagen</span>;
  };

  // Get email type display name (Issue #155)
  const getEmailTypeDisplay = (type) => {
    switch (type) {
      case 'verification':
        return 'Verifizierung';
      case 'otp':
        return 'Einmalpasswort';
      case 'password_reset':
        return 'Passwort zurücksetzen';
      case 'password_changed':
        return 'Passwort geändert';
      case 'inactivity_warning':
        return 'Inaktivitätswarnung';
      case 'content_deleted':
        return 'Inhalt gelöscht';
      default:
        return type;
    }
  };

  const activitiesTotalPages = Math.ceil(activitiesTotal / perPage);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'Benutzer nicht gefunden'}</div>
        <button className="secondary-button" onClick={onBack}>Zurück</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header with Back Button */}
      <div className="page-header">
        <div>
          <button className="secondary-button" onClick={onBack} style={{ marginBottom: '12px' }}>
            ← Zurück zur Liste
          </button>
          <h1>Kontodetails</h1>
          <p className="page-subtitle">{user.first_name} {user.last_name}</p>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Section 1: Account Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Kontoinformationen</h2>
          {!isEditing ? (
            <button
              className="action-btn edit"
              onClick={() => setIsEditing(true)}
              disabled={actionLoading}
            >
              ✏️ Bearbeiten
            </button>
          ) : (
            <div className="edit-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditData({
                    first_name: user.first_name,
                    last_name: user.last_name,
                    enrolled_university: user.enrolled_university || ''
                  });
                }}
                disabled={actionLoading}
              >
                Abbrechen
              </button>
              <button
                className="primary-button"
                onClick={handleSaveEdit}
                disabled={actionLoading}
              >
                Speichern
              </button>
            </div>
          )}
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Vorname</label>
            {isEditing ? (
              <input
                type="text"
                value={editData.first_name}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                className="filter-input"
              />
            ) : (
              <span>{user.first_name}</span>
            )}
          </div>
          <div className="info-item">
            <label>Nachname</label>
            {isEditing ? (
              <input
                type="text"
                value={editData.last_name}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                className="filter-input"
              />
            ) : (
              <span>{user.last_name}</span>
            )}
          </div>
          <div className="info-item">
            <label>Hochschule</label>
            {isEditing ? (
              <div className="autocomplete-container">
                <input
                  type="text"
                  value={editData.enrolled_university}
                  onChange={(e) => handleUniversityInputChange(e.target.value)}
                  onFocus={() => {
                    if (editData.enrolled_university.length > 0) {
                      const filtered = UNIVERSITIES.filter(uni =>
                        uni.toLowerCase().includes(editData.enrolled_university.toLowerCase())
                      );
                      setUniversitySuggestions(filtered);
                      setShowUniversitySuggestions(filtered.length > 0);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowUniversitySuggestions(false), 200)}
                  className="filter-input"
                  placeholder="z.B. TU Hamburg"
                />
                {showUniversitySuggestions && universitySuggestions.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {universitySuggestions.map((uni, index) => (
                      <li
                        key={index}
                        className="autocomplete-suggestion"
                        onClick={() => handleUniversitySelect(uni)}
                      >
                        {uni}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <span>{user.enrolled_university || '-'}</span>
            )}
          </div>
          <div className="info-item">
            <label>E-Mail</label>
            <span>{user.email}</span>
          </div>
          <div className="info-item">
            <label>Letzter Login</label>
            <span>{formatDate(user.last_login)}</span>
          </div>
          <div className="info-item">
            <label>Registriert am</label>
            <span>{formatDate(user.created_at)}</span>
          </div>
          {/* Issue #235: Display AGB acceptance date */}
          <div className="info-item">
            <label>AGB akzeptiert am</label>
            <span>{user.agb_accepted_at ? formatDate(user.agb_accepted_at) : '-'}</span>
          </div>
          {/* Issue #269: Display personalized ads consent (read-only) */}
          <div className="info-item">
            <label>Einwilligung zu personalisierten Werbungen</label>
            <span>
              {user.personalized_ads_consent === true ? 'Ja' :
               user.personalized_ads_consent === false ? 'Nein' : '-'}
            </span>
          </div>
          <div className="info-item">
            <label>Status</label>
            {getStatusBadge()}
          </div>
          {isUserSuspended() && (
            <div className="info-item">
              <label>Gesperrt bis</label>
              <span>{formatDate(user.suspended_until)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Email History (Issue #155) */}
      <section className="detail-section">
        <div className="section-header">
          <h2>E-Mails</h2>
          <span className="activities-count">{emails.length} E-Mails</span>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Betreff</th>
                <th>Typ</th>
                <th>Gesendet am</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr>
                  <td colSpan="4" className="empty-table-message">
                    Keine E-Mails gesendet
                  </td>
                </tr>
              ) : (
                emails.map((email) => (
                  <tr key={email.id} className={email.status === 'failed' ? 'reported-row' : ''}>
                    <td>{email.email_title}</td>
                    <td>{getEmailTypeDisplay(email.email_type)}</td>
                    <td>{formatDate(email.sent_at)}</td>
                    <td>{getEmailStatusBadge(email.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 3: Actions */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Aktionen</h2>
        </div>

        <div className="actions-grid">
          {/* Verification Actions */}
          <div className="action-card">
            <h3>Verifizierung</h3>
            <div className="action-buttons-group">
              {!user.email_verified ? (
                <>
                  <button
                    className="action-btn activate"
                    onClick={() => handleAction('activate')}
                    disabled={actionLoading}
                    title="Konto ohne E-Mail verifizieren"
                  >
                    ✓ Manuell verifizieren
                  </button>
                  <button
                    className="action-btn resend"
                    onClick={() => handleAction('resendVerification')}
                    disabled={actionLoading}
                    title="Verifizierungs-E-Mail erneut senden"
                  >
                    📧 Verifizierungs-E-Mail senden
                  </button>
                </>
              ) : (
                <button
                  className="action-btn deactivate"
                  onClick={() => handleAction('deactivate')}
                  disabled={actionLoading}
                  title="Verifizierung entfernen"
                >
                  ✗ Verifizierung entfernen
                </button>
              )}
            </div>
          </div>

          {/* OTP Actions */}
          <div className="action-card">
            <h3>Einmalpasswort (OTP)</h3>
            <div className="action-buttons-group">
              <button
                className="action-btn reset"
                onClick={() => setShowOtpModal(true)}
                disabled={actionLoading}
                title="OTP manuell setzen"
              >
                🔑 OTP manuell setzen
              </button>
              <button
                className="action-btn resend"
                onClick={() => handleAction('sendOtp')}
                disabled={actionLoading}
                title="OTP per E-Mail senden"
              >
                📧 OTP-E-Mail senden
              </button>
            </div>
          </div>

          {/* Suspension Actions */}
          <div className="action-card">
            <h3>Kontosperre</h3>
            <div className="action-buttons-group">
              {isUserSuspended() ? (
                <button
                  className="action-btn unsuspend"
                  onClick={() => setShowUnsuspendModal(true)}
                  disabled={actionLoading}
                  title="Sperre aufheben"
                >
                  🔓 Sperre aufheben
                </button>
              ) : (
                <button
                  className="action-btn suspend"
                  onClick={() => setShowSuspendModal(true)}
                  disabled={actionLoading}
                  title="Konto sperren"
                >
                  🔒 Konto sperren
                </button>
              )}
            </div>
          </div>

          {/* Password Reset */}
          <div className="action-card">
            <h3>Passwort</h3>
            <div className="action-buttons-group">
              <button
                className="action-btn reset"
                onClick={() => handleAction('resetPassword')}
                disabled={actionLoading}
                title="Passwort-Reset-E-Mail senden"
              >
                📧 Passwort zurücksetzen
              </button>
            </div>
          </div>

          {/* GDPR Data Export (Issue #358) */}
          <div className="action-card">
            <h3>DSGVO</h3>
            <div className="action-buttons-group">
              <button
                className="action-btn reset"
                onClick={handleGdprExport}
                disabled={exportRunning}
                title="Alle personenbezogenen Daten dieses Nutzers als ZIP herunterladen"
              >
                {exportRunning ? `Erstelle Export… ${exportPercent}%` : '📦 DSGVO-Datenexport herunterladen'}
              </button>
              {exportRunning && (
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    background: '#eee',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginTop: '8px'
                  }}
                >
                  <div
                    style={{
                      width: `${exportPercent}%`,
                      height: '100%',
                      background: '#2563eb',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              )}
              {exportError && (
                <div className="error-message" style={{ marginTop: '8px' }}>
                  {exportError}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Bearbeitungslog (Issue #159) */}
      <Bearbeitungslog targetType="user" targetId={userId} />

      {/* Section 5: Activities */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Aktivitäten</h2>
          <span className="activities-count">{activitiesTotal} Inhalte</span>
        </div>

        {/* Filters */}
        <div className="activities-filters">
          <div className="filter-row">
            <select
              className="filter-input"
              value={activitiesFilters.content_type}
              onChange={(e) => {
                setActivitiesFilters({ ...activitiesFilters, content_type: e.target.value });
                setActivitiesPage(1);
              }}
            >
              <option value="">Alle Typen</option>
              <option value="post">Beiträge</option>
              <option value="marketplace">Objekte</option>
              <option value="comment">Kommentare</option>
              <option value="course_review">Kursbewertungen</option>
            </select>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={activitiesFilters.reported_only}
                onChange={(e) => {
                  setActivitiesFilters({ ...activitiesFilters, reported_only: e.target.checked });
                  setActivitiesPage(1);
                }}
              />
              Nur gemeldete Inhalte
            </label>
          </div>

          <div className="filter-row">
            <input
              type="text"
              className="filter-input search-input-activities"
              placeholder="Inhalt suchen..."
              value={activitiesFilters.search}
              onChange={(e) => setActivitiesFilters({ ...activitiesFilters, search: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setActivitiesPage(1);
                  fetchActivities();
                }
              }}
            />
            <button
              className="search-button"
              onClick={() => {
                setActivitiesPage(1);
                fetchActivities();
              }}
            >
              Suchen
            </button>
          </div>

          <div className="filter-row date-filters">
            <div className="date-input-group">
              <label>Von</label>
              <input
                type="datetime-local"
                className="filter-input"
                value={activitiesFilters.date_from}
                onChange={(e) => {
                  setActivitiesFilters({ ...activitiesFilters, date_from: e.target.value });
                  setActivitiesPage(1);
                }}
              />
            </div>
            <div className="date-input-group">
              <label>Bis</label>
              <input
                type="datetime-local"
                className="filter-input"
                value={activitiesFilters.date_to}
                onChange={(e) => {
                  setActivitiesFilters({ ...activitiesFilters, date_to: e.target.value });
                  setActivitiesPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {/* Activities Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Inhalt</th>
                <th>Typ</th>
                <th>Anonym</th>
                <th>Erstellt am</th>
                <th>Gemeldet</th>
              </tr>
            </thead>
            <tbody>
              {activities.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-table-message">
                    Keine Aktivitäten gefunden
                  </td>
                </tr>
              ) : (
                activities.map((activity) => (
                  <tr key={activity.id} className={activity.is_reported ? 'reported-row' : ''}>
                    <td className="content-cell">{activity.content}</td>
                    <td>{getContentTypeBadge(activity.content_type)}</td>
                    <td>{activity.is_anonymous ? <span className="anonym-checkmark">✓</span> : '-'}</td>
                    <td>{formatDate(activity.created_at)}</td>
                    <td>
                      {activity.is_reported ? (
                        <span className="anonym-checkmark" title={`${activity.report_count} Meldungen`}>✓</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {activitiesTotalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setActivitiesPage(p => Math.max(1, p - 1))}
              disabled={activitiesPage === 1}
              className="pagination-btn"
            >
              ← Zurück
            </button>
            <span className="pagination-info">
              Seite {activitiesPage} von {activitiesTotalPages}
            </span>
            <button
              onClick={() => setActivitiesPage(p => Math.min(activitiesTotalPages, p + 1))}
              disabled={activitiesPage === activitiesTotalPages}
              className="pagination-btn"
            >
              Weiter →
            </button>
          </div>
        )}
      </section>

      {/* Issue #417: Suspend Confirmation Modal */}
      <SuspendUserModal
        open={showSuspendModal}
        userName={`${user.first_name} ${user.last_name}`}
        onConfirm={handleSuspendConfirm}
        onCancel={() => setShowSuspendModal(false)}
      />

      {/* Issue #417: Unsuspend Confirmation Modal */}
      <DeleteReasonModal
        open={showUnsuspendModal}
        title="Sperre aufheben"
        description={`Sie sind dabei, die Sperre des Kontos von ${user.first_name} ${user.last_name} aufzuheben.`}
        prompt="Bitte geben Sie einen Grund für das Entsperren an (mindestens 20 Zeichen):"
        placeholder="Grund für das Entsperren eingeben..."
        confirmLabel="Entsperren"
        failureFallback="Entsperren fehlgeschlagen"
        onConfirm={handleUnsuspendConfirm}
        onCancel={() => setShowUnsuspendModal(false)}
      />

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="modal-overlay" onClick={() => setShowOtpModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>OTP manuell setzen</h2>
            <p>Geben Sie ein 6-stelliges OTP ein:</p>
            <div className="form-group">
              <input
                type="text"
                value={otpValue}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpValue(value);
                }}
                placeholder="123456"
                maxLength="6"
                className="filter-input otp-input"
              />
            </div>
            <div className="form-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpValue('');
                }}
              >
                Abbrechen
              </button>
              <button
                className="primary-button"
                onClick={handleSetOtp}
                disabled={actionLoading || otpValue.length !== 6}
              >
                OTP setzen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetail;
